import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  FRAMEWORK_STEER_COPY,
  PLOT_WORKSHOP_BEATS_LABEL,
  PLOT_WORKSHOP_CHANGE_FRAMEWORK,
  PLOT_WORKSHOP_CHOOSE_FRAMEWORK,
  PLOT_WORKSHOP_EMPTY_FRAMEWORK,
  PLOT_WORKSHOP_FILL_CANCEL,
  PLOT_WORKSHOP_FILL_EDIT,
  PLOT_WORKSHOP_FILL_EDIT_FIELD,
  PLOT_WORKSHOP_FILL_HIDE,
  PLOT_WORKSHOP_FILL_SAVE,
  PLOT_WORKSHOP_FILL_SAVE_ERROR,
  PLOT_WORKSHOP_FILL_SAVING,
  PLOT_WORKSHOP_FILL_SHOW,
  editBeatDescriptionLabel,
  PRIMARY_NEXT_TIP,
  WORKSHOP_READY_HONEST,
  WORKSHOP_STATUS_NOT_READY,
  WORKSHOP_STATUS_READY,
  beatHasBlurb,
  frameworkRosterBeats,
  optionalFoldQuietCopy,
  outlinePrimaryLabel,
  stillOpenLine,
  stillOpenNames,
  workshopFrameworkSteer,
  OUTLINE_EXISTS_NEXT_STEP,
  WORKSHOP_GENERATE_BLOCKED,
  workshopIsReady,
  workshopThinWarn,
  workThisBeatLabel,
  READINESS_NOT_READY,
  READINESS_READY,
} from '../../utils/plotReadyCopy';
import {
  KEEP_AS_PLAN_READY,
  USE_FRAMEWORK_AS_PLAN,
  USE_FRAMEWORK_AS_PLAN_HELP,
} from '../../utils/writerCopy';
import './PlotReadyBoard.css';

export function holesLine(holes) {
  const names = (holes || []).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return `${names[0]} is still open.`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are still open.`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]} are still open.`;
}

function beatFieldId(name) {
  return `beat-fill-${String(name || 'beat').trim().replace(/\s+/g, '-')}`;
}

export function confirmedStubTexts(beat) {
  const raw = beat?.confirmed_scene_stubs;
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.confirmed === false) {
    return [];
  }
  const rows = Array.isArray(raw) ? raw : raw?.stubs;
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => (typeof row === 'string' ? row : row?.text))
    .map((text) => String(text || '').trim())
    .filter(Boolean);
}

function FrameworkBeatRow({ beat, onWorkBeat, onSaveFill }) {
  const [fillOpen, setFillOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const name = beat?.name || 'Sequence';
  const purpose = (beat?.purpose || '').trim();
  const content = (beat?.content || '').trim();
  const [shown, setShown] = useState(content);
  const editingRef = useRef(false);
  editingRef.current = editing;
  const hasFill = beatHasBlurb(beat);
  const stubTexts = confirmedStubTexts(beat);
  const canWork = Boolean(String(beat?.name || '').trim());
  const canEdit = hasFill && stubTexts.length === 0 && typeof onSaveFill === 'function';

  useEffect(() => {
    if (!editingRef.current) setShown(content);
  }, [content]);

  const startEdit = () => {
    setDraft(shown);
    setSaveError('');
    setEditing(true);
    setFillOpen(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(shown);
    setSaveError('');
  };

  const saveEdit = async () => {
    const next = draft.trim();
    if (!next || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      await onSaveFill({ ...beat, name, content: next });
      setShown(next);
      setEditing(false);
    } catch (err) {
      const message = String(err?.message || '').trim();
      setSaveError(message || PLOT_WORKSHOP_FILL_SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="plot-ready-board__beat">
      <div className="plot-ready-board__beat-row">
        {hasFill ? (
          <button
            type="button"
            className="plot-ready-board__beat-expand"
            aria-expanded={fillOpen}
            aria-label={fillOpen ? PLOT_WORKSHOP_FILL_HIDE : PLOT_WORKSHOP_FILL_SHOW}
            data-testid={`workshop-beat-fill-${name}`}
            onClick={() => setFillOpen((value) => !value)}
          >
            {fillOpen ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
          </button>
        ) : (
          <span className="plot-ready-board__beat-expand-spacer" aria-hidden="true" />
        )}
        <div className="plot-ready-board__beat-main">
          <div className="plot-ready-board__beat-head">
            <h3 className="plot-ready-board__beat-name">{name}</h3>
            {canWork ? (
              <button
                type="button"
                className="cd-btn"
                onClick={() => onWorkBeat?.(beat)}
              >
                {workThisBeatLabel(name)}
              </button>
            ) : null}
          </div>
          {purpose ? (
            <p className="plot-ready-board__purpose">{purpose}</p>
          ) : null}
          {hasFill && fillOpen && editing ? (
            <div className="plot-ready-board__fill-editor">
              <label className="plot-ready-board__fill-label" htmlFor={beatFieldId(name)}>
                {PLOT_WORKSHOP_FILL_EDIT_FIELD}
              </label>
              <textarea
                id={beatFieldId(name)}
                className="plot-ready-board__fill-input"
                aria-label={`${PLOT_WORKSHOP_FILL_EDIT_FIELD}: ${name}`}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />
              <div className="plot-ready-board__fill-actions">
                <button
                  type="button"
                  className="cd-btn"
                  disabled={saving || !draft.trim()}
                  onClick={saveEdit}
                >
                  {saving ? PLOT_WORKSHOP_FILL_SAVING : PLOT_WORKSHOP_FILL_SAVE}
                </button>
                <button
                  type="button"
                  className="cd-btn"
                  disabled={saving}
                  onClick={cancelEdit}
                >
                  {PLOT_WORKSHOP_FILL_CANCEL}
                </button>
              </div>
              {saveError ? (
                <p className="plot-ready-board__error" role="alert">{saveError}</p>
              ) : null}
            </div>
          ) : null}
          {hasFill && fillOpen && !editing ? (
            <>
              {stubTexts.length > 0 ? (
                <ol className="plot-ready-board__stubs" aria-label={`Scene stubs for ${name}`}>
                  {stubTexts.map((text, index) => (
                    <li key={`${index}-${text}`}>{text}</li>
                  ))}
                </ol>
              ) : (
                <p className="plot-ready-board__blurb">{shown}</p>
              )}
              {canEdit ? (
                <button
                  type="button"
                  className="cd-btn plot-ready-board__fill-edit"
                  aria-label={editBeatDescriptionLabel(name)}
                  onClick={startEdit}
                >
                  {PLOT_WORKSHOP_FILL_EDIT}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function PlotReadyBoard({
  workshop,
  assessment,
  onWorkBeat,
  onSaveFill,
  onOpenPlottrack,
  onChooseFramework,
  onUseFrameworkAsPlan,
  hasOutline = false,
}) {
  const view = workshop || assessment || {};
  const loaded = workshop != null || assessment != null;
  const ready = workshopIsReady(view);
  const roster = frameworkRosterBeats(view);
  const hasFramework = Boolean(view.has_framework) && !view.empty_framework;
  const frameworkName = String(view.framework_name || '').trim();
  const showBeatList = hasFramework && roster.length > 0;
  const emptyFramework = Boolean(view.empty_framework || !view.has_framework);
  const outlineReady = Boolean(view.has_outline || hasOutline);
  const quietFoldCopy = optionalFoldQuietCopy({ ...view, has_outline: outlineReady });
  const warning = outlineReady ? '' : workshopThinWarn(view);
  const frameworkCopy = workshopFrameworkSteer(view);
  const openNames = stillOpenNames(view);
  const openLine = stillOpenLine(openNames);
  const readinessSteps = (Array.isArray(view.ready_steps) ? view.ready_steps : [])
    .filter((step) => step && step.name);
  const primaryLabel = outlinePrimaryLabel(outlineReady);
  const keepAsPlan = view.plan_mode === 'keep_as_plan' && Boolean(view.has_imported_plan);
  const honestLine = keepAsPlan && ready
    ? KEEP_AS_PLAN_READY
    : (ready ? WORKSHOP_READY_HONEST : (openLine || PRIMARY_NEXT_TIP));
  const generateClosed = !ready && !outlineReady;
  const statusLabel = outlineReady
    ? OUTLINE_EXISTS_NEXT_STEP
    : (ready ? WORKSHOP_STATUS_READY : WORKSHOP_STATUS_NOT_READY);

  return (
    <div className="plot-ready-board plot-ready-board--workshop">
      {loaded && hasFramework ? (
        <section
          className="plot-ready-board__beats-manager"
          aria-label={PLOT_WORKSHOP_BEATS_LABEL}
          data-testid="workshop-beats-manager"
        >
          <header
            className="plot-ready-board__beats-head"
            data-testid="workshop-chosen-framework"
          >
            <h2
              className="plot-ready-board__framework-name"
              data-testid="workshop-framework-name"
            >
              {frameworkName || PLOT_WORKSHOP_BEATS_LABEL}
            </h2>
            <button
              type="button"
              className="plot-ready-board__change-framework"
              onClick={onChooseFramework}
            >
              {PLOT_WORKSHOP_CHANGE_FRAMEWORK}
            </button>
          </header>
          {quietFoldCopy ? (
            <p className="plot-ready-board__quiet" data-testid="workshop-beats-quiet">
              {quietFoldCopy}
            </p>
          ) : null}
          {showBeatList ? (
            <div className="plot-ready-board__beat-list" data-testid="workshop-beat-list">
              {roster.map((beat) => (
                <FrameworkBeatRow
                  key={beat.name}
                  beat={beat}
                  onWorkBeat={onWorkBeat}
                  onSaveFill={onSaveFill}
                />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {loaded && emptyFramework ? (
        <section
          className="plot-ready-board__choose-framework"
          aria-label={PLOT_WORKSHOP_CHOOSE_FRAMEWORK}
          data-testid="workshop-choose-framework"
        >
          <p>{frameworkCopy || PLOT_WORKSHOP_EMPTY_FRAMEWORK}</p>
          <p className="plot-ready-board__why" role="note">{FRAMEWORK_STEER_COPY}</p>
          <button
            type="button"
            className="cd-btn"
            onClick={onChooseFramework}
          >
            {PLOT_WORKSHOP_CHOOSE_FRAMEWORK}
          </button>
        </section>
      ) : null}

      {loaded ? (
        <section
          className="plot-ready-board__ready-strip"
          aria-label={statusLabel}
          data-testid="workshop-ready-strip"
        >
          <div className="plot-ready-board__ready-row">
            <h2
              className="plot-ready-board__status"
              data-testid="workshop-status"
            >
              {statusLabel}
            </h2>
            <button
              type="button"
              className={ready ? 'cd-btn cd-btn--primary' : 'cd-btn'}
              data-testid="workshop-outline-primary"
              disabled={generateClosed}
              aria-describedby={generateClosed ? 'workshop-generate-blocked' : undefined}
              onClick={() => {
                if (generateClosed) return;
                onOpenPlottrack?.();
              }}
            >
              {primaryLabel}
            </button>
          </div>
          {outlineReady ? null : (
            <>
              {readinessSteps.length > 0 ? (
                <ul className="plot-ready-board__readiness" data-testid="workshop-readiness">
                  {readinessSteps.map((step) => (
                    <li
                      key={step.id || step.name}
                      data-state={step.done ? 'ready' : 'not-ready'}
                    >
                      <span className="plot-ready-board__readiness-name">{step.name}</span>
                      <span className="plot-ready-board__readiness-state">
                        {step.done ? READINESS_READY : READINESS_NOT_READY}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {ready ? (
                <p className="plot-ready-board__help" data-testid="workshop-ready-honest">
                  {honestLine}
                </p>
              ) : readinessSteps.length > 0 ? null : (
                <p
                  className="plot-ready-board__still-open"
                  data-testid={openLine ? 'workshop-still-open' : 'workshop-ready-honest'}
                >
                  {honestLine}
                </p>
              )}
              {!ready ? (
                <p
                  id="workshop-generate-blocked"
                  className="plot-ready-board__why"
                  data-testid="workshop-generate-blocked"
                >
                  {WORKSHOP_GENERATE_BLOCKED}
                </p>
              ) : null}
              {warning ? (
                <p className="plot-ready-board__why" role="status">{warning}</p>
              ) : null}
            </>
          )}
          {keepAsPlan ? (
            <div className="plot-ready-board__plan" data-testid="workshop-keep-as-plan">
              <button
                type="button"
                className="cd-btn"
                data-testid="workshop-use-framework-plan"
                onClick={() => onUseFrameworkAsPlan?.()}
              >
                {USE_FRAMEWORK_AS_PLAN}
              </button>
              <p className="plot-ready-board__help">{USE_FRAMEWORK_AS_PLAN_HELP}</p>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
